import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import classNames from 'classnames';
import {
  Badge,
  Button,
  Form,
  Modal,
} from 'react-bootstrap';
import AdminSidebar from '../../components/AdminSidebar';
import {
  AdminNotificationsProvider,
  useAdminNotifications,
} from '../../components/AdminNotifications';
import useAdminGuard from '../../hooks/useAdminGuard';
import { hasPermission, PERMISSION_IDS } from '../../utils/permissions';
import { buildApiUrl } from '../../config/apiConfig';
import '../../styles/admin.css';
import './AdminBundlesPage.css';
import { logError } from '../../utils/logger';
import { formatARS } from '../../utils/currency';

const BundleSkeleton = () => (
  <div className="ABP-skeleton">
    <div className="ABP-skeleton-body">
      <div className="ABP-skeleton-line ABP-skeleton-line--short" />
      <div className="ABP-skeleton-line ABP-skeleton-line--medium" />
      <div className="ABP-skeleton-line ABP-skeleton-line--long" />
    </div>
  </div>
);

const StatsCard = ({ icon, label, value, variant }) => (
  <div className="ABP-stat-card">
    <div className={classNames('ABP-stat-icon', `ABP-stat-icon--${variant}`)}>
      <i className={`bi ${icon}`} aria-hidden="true" />
    </div>
    <div className="ABP-stat-content">
      <div className="ABP-stat-value">{value}</div>
      <div className="ABP-stat-label">{label}</div>
    </div>
  </div>
);

const EmptyState = ({ onCreate }) => (
  <div className="ABP-empty-state">
    <i className="bi-boxes ABP-empty-icon" />
    <h5 className="ABP-empty-title">Aún no hay bundles</h5>
    <p className="ABP-empty-description">
      Crea tu primer bundle combinando múltiples productos con descuentos especiales.
    </p>
    {onCreate && (
      <Button variant="primary" onClick={onCreate}>
        <i className="bi-plus-circle me-2" />
        Crear bundle
      </Button>
    )}
  </div>
);

const AdminBundlesPageInner = () => {
  const { token, user } = useAdminGuard();
  const { notify } = useAdminNotifications();
  const navigate = useNavigate();

  // Verificar permisos
  const hasViewBundles = useMemo(() => hasPermission(user, PERMISSION_IDS.VER_BUNDLES), [user]);
  const hasCreateBundles = useMemo(() => hasPermission(user, PERMISSION_IDS.CREAR_BUNDLES), [user]);

  // Si no tiene permiso, redirigir
  useEffect(() => {
    if (user && !hasViewBundles) {
      notify({
        title: 'Acceso denegado',
        message: 'No tienes permisos para acceder a la gestión de bundles.',
        variant: 'error',
      });
      navigate('/403', { replace: true });
    }
  }, [user, hasViewBundles, navigate, notify]);

  const [stats, setStats] = useState({
    total: 0,
    active: 0,
    inactive: 0
  });
  const [bundles, setBundles] = useState([]);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [loadingStats, setLoadingStats] = useState(true);
  const [loadingBundles, setLoadingBundles] = useState(true);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [createBundleData, setCreateBundleData] = useState({
    nombre: '',
    descripcion: '',
    price: '',
    discountPercent: '',
    status: 'inactive'
  });
  const [creatingBundle, setCreatingBundle] = useState(false);

  const fetchStats = async (isRefresh = false) => {
    if (!token) return;
    if (!isRefresh) setLoadingStats(true);
    try {
      const response = await fetch(buildApiUrl('/api/bundles/stats'), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`Error al cargar estadísticas (${response.status})`);
      }
      const data = await response.json();
      setStats(data.stats || stats);
    } catch (error) {
      logError('[AdminBundlesPage] Error fetching stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const fetchBundles = async (isRefresh = false) => {
    if (!token) return;
    if (!isRefresh) setLoadingBundles(true);
    try {
      const response = await fetch(buildApiUrl('/api/bundles'), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      
      if (!response.ok) {
        throw new Error(`Error al cargar bundles (${response.status})`);
      }
      const data = await response.json();
      setBundles(Array.isArray(data.bundles) ? data.bundles : []);
    } catch (error) {
      logError('[AdminBundlesPage] Error fetching bundles:', error);
    } finally {
      setLoadingBundles(false);
    }
  };

  useEffect(() => {
    if (!token) return;
    let isMounted = true;
    
    const loadData = async () => {
      if (isMounted) {
        await Promise.all([fetchStats(), fetchBundles()]);
      }
    };
    
    loadData();
    
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const handleRefresh = async () => {
    setIsRefreshing(true);
    await Promise.all([fetchStats(true), fetchBundles(true)]);
    setIsRefreshing(false);
    notify({
      title: 'Datos actualizados',
      message: 'La lista de bundles se sincronizó correctamente.',
      variant: 'success',
    });
  };

  const handleCreateBundle = async (e) => {
    e.preventDefault();
    if (!createBundleData.nombre.trim() || !createBundleData.price) {
      notify({
        title: 'Datos incompletos',
        message: 'Nombre y precio son requeridos.',
        variant: 'warning',
      });
      return;
    }

    setCreatingBundle(true);
    try {
      const response = await fetch(buildApiUrl('/api/bundles'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          nombre: createBundleData.nombre.trim(),
          descripcion: createBundleData.descripcion || null,
          price: parseFloat(createBundleData.price),
          discountPercent: createBundleData.discountPercent ? parseFloat(createBundleData.discountPercent) : 0,
          status: createBundleData.status
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ error: 'Error desconocido' }));
        throw new Error(errorData.error || `Error al crear bundle (${response.status})`);
      }

      notify({
        title: 'Bundle creado',
        message: 'El bundle se creó exitosamente.',
        variant: 'success',
      });

      setShowCreateModal(false);
      setCreateBundleData({ nombre: '', descripcion: '', price: '', discountPercent: '', status: 'inactive' });
      await Promise.all([fetchStats(true), fetchBundles(true)]);
    } catch (error) {
      notify({
        title: 'Error al crear bundle',
        message: error.message,
        variant: 'error',
      });
    } finally {
      setCreatingBundle(false);
    }
  };

  const filteredBundles = useMemo(() => {
    let filtered = bundles;
    
    if (statusFilter !== 'all') {
      filtered = filtered.filter(b => b.status === statusFilter);
    }
    
    if (search.trim()) {
      const term = search.trim().toLowerCase();
      filtered = filtered.filter((bundle) => {
        const nombre = bundle.nombre || '';
        const descripcion = bundle.descripcion || '';
        return (
          nombre.toLowerCase().includes(term) ||
          descripcion.toLowerCase().includes(term) ||
          String(bundle.id).includes(term)
        );
      });
    }
    
    return filtered;
  }, [bundles, statusFilter, search]);

  return (
    <div className="admin-dashboard">
      <div className="admin-layout">
        <AdminSidebar
          activeKey="bundles"
          onNavigate={(item) => {
            if (item.path) {
              navigate(item.path);
              return;
            }
          }}
        />
        <main className="admin-main">
          <div className="ABP-container">
            <div className="ABP-header">
              <div className="ABP-header-content">
                <h1 className="ABP-title">
                  <i className="bi-boxes text-primary" />
                  Gestión de Bundles
                </h1>
                <p className="ABP-subtitle">
                  Crea paquetes de productos con descuentos especiales para tus clientes.
                </p>
              </div>
              <div className="ABP-actions">
                {hasCreateBundles && (
                  <Button variant="primary" onClick={() => setShowCreateModal(true)}>
                    <i className="bi-plus-circle me-2" />
                    Crear bundle
                  </Button>
                )}
                <Button variant="outline-primary" onClick={handleRefresh} disabled={isRefreshing}>
                  {isRefreshing ? (
                    <>
                      <span className="spinner-border spinner-border-sm me-2" />
                      Actualizando
                    </>
                  ) : (
                    <>
                      <i className="bi-arrow-repeat me-2" />
                      Actualizar
                    </>
                  )}
                </Button>
              </div>
            </div>

            <section className="ABP-stats-section">
              <div className="ABP-stats-grid">
                <StatsCard
                  icon="bi-boxes"
                  label="Total bundles"
                  value={loadingStats ? '--' : stats.total}
                  variant="primary"
                />
                <StatsCard
                  icon="bi-check-circle-fill"
                  label="Activos"
                  value={loadingStats ? '--' : stats.active}
                  variant="success"
                />
                <StatsCard
                  icon="bi-pause-circle-fill"
                  label="Inactivos"
                  value={loadingStats ? '--' : stats.inactive}
                  variant="warning"
                />
              </div>
            </section>

            <section className="ABP-filters-section">
              <div className="ABP-filters-card">
                <div className="ABP-filters-content">
                  <Form.Control
                    type="search"
                    placeholder="Buscar por nombre o descripción..."
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    className="ABP-search-input"
                  />
                  <Form.Select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="ABP-filter-select"
                  >
                    <option value="all">Todos los estados</option>
                    <option value="active">Activos</option>
                    <option value="inactive">Inactivos</option>
                  </Form.Select>
                  {search && (
                    <Button
                      variant="outline-secondary"
                      onClick={() => setSearch('')}
                      size="sm"
                    >
                      Limpiar
                    </Button>
                  )}
                </div>
              </div>
            </section>

            <section className="ABP-bundles-section">
              {loadingBundles ? (
                <div className="ABP-bundles-grid">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <BundleSkeleton key={index} />
                  ))}
                </div>
              ) : filteredBundles.length === 0 ? (
                <EmptyState onCreate={hasCreateBundles ? () => setShowCreateModal(true) : null} />
              ) : (
                <div className="ABP-bundles-grid">
                  {filteredBundles.map((bundle) => (
                    <div key={bundle.id} className="ABP-bundle-card">
                      <div className="ABP-bundle-header">
                        <Badge bg={bundle.status === 'active' ? 'success' : 'secondary'}>
                          {bundle.status === 'active' ? 'Activo' : 'Inactivo'}
                        </Badge>
                      </div>
                      <div className="ABP-bundle-body">
                        <h3 className="ABP-bundle-title">{bundle.nombre || 'Sin nombre'}</h3>
                        <p className="ABP-bundle-description">
                          {bundle.descripcion || 'Sin descripción disponible.'}
                        </p>
                        <div className="ABP-bundle-info">
                          <div className="ABP-bundle-price">
                            <span className="ABP-price-label">Precio:</span>
                            <span className="ABP-price-value">
                              {formatARS(parseFloat(bundle.price || 0))}
                            </span>
                          </div>
                          {bundle.discountPercent > 0 && (
                            <Badge bg="info" className="ABP-discount-badge">
                              -{bundle.discountPercent}% descuento
                            </Badge>
                          )}
                          {bundle.productsCount !== undefined && (
                            <div className="ABP-bundle-products">
                              <i className="bi-box-seam me-1" />
                              {bundle.productsCount} producto{bundle.productsCount !== 1 ? 's' : ''}
                            </div>
                          )}
                        </div>
                      </div>
                      <div className="ABP-bundle-footer">
                        <Button
                          variant="primary"
                          className="ABP-bundle-action"
                          onClick={() => navigate(`/admin/bundles/${bundle.id}`)}
                        >
                          <i className="bi-box-arrow-in-right me-2" />
                          Ver detalle
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </section>
          </div>
        </main>
      </div>

      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi-plus-circle me-2" />
            Crear Nuevo Bundle
          </Modal.Title>
        </Modal.Header>
        <Form onSubmit={handleCreateBundle}>
          <Modal.Body>
            <Form.Group className="mb-3">
              <Form.Label>Nombre del Bundle *</Form.Label>
              <Form.Control
                type="text"
                placeholder="Ingresa el nombre del bundle"
                value={createBundleData.nombre}
                onChange={(e) => setCreateBundleData({ ...createBundleData, nombre: e.target.value })}
                required
                autoFocus
                maxLength={100}
                disabled={creatingBundle}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Descripción</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                placeholder="Descripción del bundle"
                value={createBundleData.descripcion}
                onChange={(e) => setCreateBundleData({ ...createBundleData, descripcion: e.target.value })}
                disabled={creatingBundle}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Precio *</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                placeholder="0.00"
                value={createBundleData.price}
                onChange={(e) => setCreateBundleData({ ...createBundleData, price: e.target.value })}
                required
                disabled={creatingBundle}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Descuento (%)</Form.Label>
              <Form.Control
                type="number"
                step="0.01"
                min="0"
                max="100"
                placeholder="0"
                value={createBundleData.discountPercent}
                onChange={(e) => setCreateBundleData({ ...createBundleData, discountPercent: e.target.value })}
                disabled={creatingBundle}
              />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Estado</Form.Label>
              <Form.Select
                value={createBundleData.status}
                onChange={(e) => setCreateBundleData({ ...createBundleData, status: e.target.value })}
                disabled={creatingBundle}
              >
                <option value="inactive">Inactivo</option>
                <option value="active">Activo</option>
              </Form.Select>
            </Form.Group>
          </Modal.Body>
          <Modal.Footer>
            <Button variant="secondary" onClick={() => setShowCreateModal(false)} disabled={creatingBundle}>
              Cancelar
            </Button>
            <Button variant="primary" type="submit" disabled={creatingBundle || !createBundleData.nombre.trim() || !createBundleData.price}>
              {creatingBundle ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2" />
                  Creando...
                </>
              ) : (
                <>
                  <i className="bi-check-circle me-2" />
                  Crear Bundle
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </div>
  );
};

const AdminBundlesPage = () => (
  <AdminNotificationsProvider>
    <AdminBundlesPageInner />
  </AdminNotificationsProvider>
);

export default AdminBundlesPage;

